/**
 * React hook over the shared TMR Socket.IO manager.
 * One connection per tab; automatic exponential-backoff reconnect,
 * heartbeat zombie detection, and re-subscribe on recovery.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { tmrSocket } from '../services/tmrSocketManager';

/**
 * Subscribe to the shared TMR socket.
 * @param {object} opts
 * @param {string[]} [opts.symbols] symbols to join rooms for
 * @param {boolean} [opts.enabled=true]
 * @param {string} [opts.wsUrl]
 * @param {(status: object) => void} [opts.onStatus]
 * @param {(update: object) => void} [opts.onOhlc] live:ohlc handler
 * @param {(update: object) => void} [opts.onOc] live:oc handler
 * @param {(update: object) => void} [opts.onTick] live:tick handler
 */
export function useTmrSocket({
  symbols = [],
  enabled = true,
  wsUrl = '',
  onStatus,
  onOhlc,
  onOc,
  onTick,
} = {}) {
  const [status, setStatus] = useState(() => ({
    socket: 'idle',
    mode: 'idle',
    connected: false,
    phase: 'idle',
  }));

  const onStatusRef = useRef(onStatus);
  const onOhlcRef = useRef(onOhlc);
  const onOcRef = useRef(onOc);
  const onTickRef = useRef(onTick);
  onStatusRef.current = onStatus;
  onOhlcRef.current = onOhlc;
  onOcRef.current = onOc;
  onTickRef.current = onTick;

  // Stable symbol key so effect only re-runs when the set changes
  const symbolKey = (symbols || []).map((s) => String(s).toUpperCase()).filter(Boolean).sort().join('|');

  const handleStatus = useCallback((patch) => {
    setStatus((prev) => {
      const next = {
        ...prev,
        ...patch,
        connected: (patch.socket ?? patch.phase ?? prev.socket) === 'connected',
      };
      onStatusRef.current?.(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      handleStatus({ socket: 'idle', mode: 'idle', phase: 'idle', connected: false });
      return undefined;
    }

    const symList = symbolKey ? symbolKey.split('|') : [];

    const release = tmrSocket.acquire({
      symbols: symList,
      wsUrl,
      onStatus: handleStatus,
      onOhlc: (u) => onOhlcRef.current?.(u),
      onOc: (u) => onOcRef.current?.(u),
      onTick: (u) => onTickRef.current?.(u),
    });

    return () => {
      release();
    };
  }, [enabled, wsUrl, symbolKey, handleStatus]);

  return {
    status,
    connected: status.connected || status.socket === 'connected' || status.phase === 'connected',
    socket: tmrSocket.getSocket(),
    forceReconnect: (reason) => tmrSocket.forceReconnect(reason),
    manager: tmrSocket,
  };
}

/** Imperative access to the shared Socket.IO instance (may be null). */
export function getTmrSocket() {
  return tmrSocket.getSocket();
}

/** Imperative access to the manager (forceReconnect, getStatus, …). */
export function getTmrSocketManager() {
  return tmrSocket;
}

export { backoffMs } from '../services/tmrSocketManager';
